import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Card, CircularProgress, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import EditOutlined from '@mui/icons-material/EditOutlined';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import MyLocationRounded from '@mui/icons-material/MyLocationRounded';
import { Autocomplete, GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';

import Button from './Button';
import Modal from './Modal';
import {
  addPilotUserAddress,
  deletePilotUserAddress,
  getPilotUserAddresses,
  updatePilotUserAddress,
} from '../services/addresses';
import { V2ApiError } from '../services/client';
import { getCurrentPosition, requestLocationPermission } from '../platform/geolocation';
import { useV2Session } from '../state/SessionProvider';

const MAP_LIBRARIES = ['places'];
const searchInputStyle = {
  boxSizing: 'border-box',
  border: '1px solid #ccc',
  width: '100%',
  maxWidth: '100%',
  height: 40,
  padding: '0 12px',
  borderRadius: 4,
  fontSize: 14,
  outline: 'none',
};

const emptyForm = {
  userName: '',
  houseDetails: '',
  streetDetails: '',
  landmark: '',
  pincode: '',
  lat: '',
  lng: '',
};

function validateAddressForm(form, mode) {
  const errors = {};
  if (!String(form.userName || '').trim()) errors.userName = 'Name is required';
  if (!String(form.houseDetails || '').trim()) errors.houseDetails = 'House / flat is required';
  const pin = String(form.pincode || '').trim();
  if (!/^\d{6}$/.test(pin)) errors.pincode = 'Enter a valid 6-digit pincode';
  if (mode === 'edit') {
    const lat = Number(form.lat);
    const lng = Number(form.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      errors.location = 'Enter valid latitude and longitude';
    } else if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      errors.location = 'Coordinates are out of range';
    }
  }
  return errors;
}

function addressPreview(addr) {
  return [addr.houseDetails, addr.streetDetails, addr.landmark, addr.pincode].filter(Boolean).join(', ');
}

export default function ProfileAddresses() {
  const { mobile } = useV2Session();
  const mapKey = (process.env.REACT_APP_MAP_KEY || '').trim();
  const { isLoaded: isMapsLoaded } = useJsApiLoader({
    id: 'google-map-v2-profile-address-edit',
    googleMapsApiKey: mapKey,
    libraries: MAP_LIBRARIES,
  });
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [mode, setMode] = useState('add');
  const [form, setForm] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [formErrorMessage, setFormErrorMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [locating, setLocating] = useState(false);
  const [placeSearch, setPlaceSearch] = useState(null);
  const [editingAddressId, setEditingAddressId] = useState(null);

  const [deletingAddressId, setDeletingAddressId] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [pendingDeleteAddressId, setPendingDeleteAddressId] = useState(null);

  const loadAddresses = useCallback(async () => {
    if (!mobile) {
      setAddresses([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setListError('');
    try {
      const list = await getPilotUserAddresses(mobile);
      setAddresses(Array.isArray(list) ? list : []);
    } catch (err) {
      setListError(err instanceof V2ApiError ? err.message : 'Could not load addresses.');
      setAddresses([]);
    } finally {
      setLoading(false);
    }
  }, [mobile]);

  useEffect(() => {
    loadAddresses();
  }, [loadAddresses]);

  const closeModal = () => {
    setModalOpen(false);
    setMode('add');
    setEditingAddressId(null);
    setForm(emptyForm);
    setFormErrors({});
    setFormErrorMessage('');
    setPlaceSearch(null);
  };

  const openAdd = () => {
    setMode('add');
    setEditingAddressId(null);
    setForm(emptyForm);
    setFormErrors({});
    setFormErrorMessage('');
    setPlaceSearch(null);
    setModalOpen(true);
  };

  const openEdit = (address) => {
    setMode('edit');
    setEditingAddressId(String(address.addressId || ''));
    setForm({
      userName: address.userName || '',
      houseDetails: address.houseDetails || '',
      streetDetails: address.streetDetails || '',
      landmark: address.landmark || '',
      pincode: address.pincode || '',
      lat: Number.isFinite(Number(address.latLong?.lat)) ? String(address.latLong.lat) : '',
      lng: Number.isFinite(Number(address.latLong?.lng)) ? String(address.latLong.lng) : '',
    });
    setFormErrors({});
    setFormErrorMessage('');
    setModalOpen(true);
  };

  const onChange = (field) => (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: undefined }));
    setFormErrorMessage('');
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!mobile) return;
    const nextErrors = validateAddressForm(form, mode);
    setFormErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const payload = {
      userName: String(form.userName).trim().replace(/'/g, ''),
      houseDetails: String(form.houseDetails).trim().replace(/'/g, ''),
      streetDetails: String(form.streetDetails || '').trim().replace(/'/g, ''),
      landmark: String(form.landmark || '').trim().replace(/'/g, ''),
      pincode: String(form.pincode).trim(),
    };

    setSaving(true);
    setFormErrorMessage('');
    try {
      if (mode === 'edit' && editingAddressId) {
        const lat = Number(form.lat);
        const lng = Number(form.lng);
        await updatePilotUserAddress(mobile, editingAddressId, {
          ...payload,
          latLong: { lat, lng },
        });
      } else {
        await addPilotUserAddress(mobile, payload);
      }
      await loadAddresses();
      closeModal();
    } catch (err) {
      setFormErrorMessage(err instanceof V2ApiError ? err.message : 'Could not save address.');
    } finally {
      setSaving(false);
    }
  };

  const onUseCurrentLocation = async () => {
    setFormErrorMessage('');
    setFormErrors((prev) => ({ ...prev, location: undefined }));
    setLocating(true);
    try {
      const permission = await requestLocationPermission();
      if (permission !== 'granted') {
        setFormErrorMessage('Location permission is required to fetch your current location.');
        return;
      }
      const pos = await getCurrentPosition();
      if (!pos) {
        setFormErrorMessage('Could not read your location. Try again or enter coordinates manually.');
        return;
      }
      setForm((prev) => ({ ...prev, lat: String(pos.lat), lng: String(pos.lng) }));
    } finally {
      setLocating(false);
    }
  };

  const onPlaceChanged = () => {
    if (!placeSearch) return;
    const place = placeSearch.getPlace();
    const loc = place?.geometry?.location;
    if (!loc) {
      setFormErrors((prev) => ({ ...prev, location: 'Select a place from suggestions' }));
      return;
    }
    setForm((prev) => ({ ...prev, lat: String(loc.lat()), lng: String(loc.lng()) }));
    setFormErrors((prev) => ({ ...prev, location: undefined }));
    setFormErrorMessage('');
  };

  const requestDelete = (addressId) => {
    if (!mobile || !addressId) return;
    setPendingDeleteAddressId(String(addressId));
    setDeleteConfirmOpen(true);
  };

  const closeDeleteConfirm = () => {
    if (deletingAddressId) return;
    setDeleteConfirmOpen(false);
    setPendingDeleteAddressId(null);
  };

  const onDelete = async () => {
    const addressId = pendingDeleteAddressId;
    if (!mobile || !addressId) return;
    setDeletingAddressId(String(addressId));
    try {
      await deletePilotUserAddress(mobile, String(addressId));
      await loadAddresses();
      setDeleteConfirmOpen(false);
      setPendingDeleteAddressId(null);
    } catch (err) {
      setListError(err instanceof V2ApiError ? err.message : 'Could not delete address.');
    } finally {
      setDeletingAddressId(null);
    }
  };

  const sortedAddresses = useMemo(() => {
    return [...addresses].sort((a, b) => Number(b.timeStamp || 0) - Number(a.timeStamp || 0));
  }, [addresses]);
  const selectedLat = Number(form.lat);
  const selectedLng = Number(form.lng);
  const hasSelectedLocation = Number.isFinite(selectedLat) && Number.isFinite(selectedLng);

  if (!mobile) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        Sign in to manage addresses.
      </Alert>
    );
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.25 }}>
        Addresses
      </Typography>

      {listError ? (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {listError}
        </Alert>
      ) : null}

      {loading ? (
        <Box display="flex" justifyContent="center" py={2}>
          <CircularProgress size={28} />
        </Box>
      ) : sortedAddresses.length === 0 ? (
        <Box
          sx={{
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 2,
            py: 2.5,
            px: 2,
            textAlign: 'center',
            mb: 1.5,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No saved addresses yet.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mb: 1.5 }}>
          {sortedAddresses.map((addr) => {
            const addressId = String(addr.addressId || '');
            const deleting = deletingAddressId === addressId;
            return (
              <Card key={addressId} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.25 }}>
                  {addr.userName || 'Address'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45 }}>
                  {addressPreview(addr) || '-'}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5, mt: 1 }}>
                  <Tooltip title="Edit address">
                    <span>
                      <IconButton size="small" onClick={() => openEdit(addr)} aria-label="Edit address">
                        <EditOutlined fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={deleting ? 'Deleting...' : 'Delete address'}>
                    <span>
                      <IconButton
                        size="small"
                        color="error"
                        disabled={deleting}
                        onClick={() => requestDelete(addressId)}
                        aria-label="Delete address"
                      >
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Card>
            );
          })}
        </Box>
      )}

      <Button variant="outlined" fullWidth onClick={openAdd} sx={{ borderRadius: '5px' }}>
        Add new address
      </Button>

      <Modal open={modalOpen} onClose={closeModal} title={mode === 'edit' ? 'Edit address' : 'Add address'}>
        <Box component="form" onSubmit={onSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {formErrorMessage ? <Alert severity="error">{formErrorMessage}</Alert> : null}
          <TextField
            label="Full name"
            value={form.userName}
            onChange={onChange('userName')}
            error={Boolean(formErrors.userName)}
            helperText={formErrors.userName}
            fullWidth
          />
          <TextField
            label="House / flat / door"
            value={form.houseDetails}
            onChange={onChange('houseDetails')}
            error={Boolean(formErrors.houseDetails)}
            helperText={formErrors.houseDetails}
            fullWidth
          />
          <TextField
            label="Street / area"
            value={form.streetDetails}
            onChange={onChange('streetDetails')}
            fullWidth
          />
          <TextField label="Landmark" value={form.landmark} onChange={onChange('landmark')} fullWidth />
          <TextField
            label="Pincode"
            value={form.pincode}
            onChange={onChange('pincode')}
            inputProps={{ maxLength: 6, inputMode: 'numeric' }}
            error={Boolean(formErrors.pincode)}
            helperText={formErrors.pincode}
            fullWidth
          />
          {mode === 'edit' ? (
            <>
              <Button
                type="button"
                variant="outlined"
                size="small"
                startIcon={locating ? <CircularProgress size={14} color="inherit" /> : <MyLocationRounded />}
                disabled={locating}
                onClick={onUseCurrentLocation}
                sx={{ borderRadius: '5px', alignSelf: 'stretch' }}
              >
                {locating ? 'Locating…' : 'Use current location'}
              </Button>
              {mapKey ? (
                isMapsLoaded ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Autocomplete onLoad={setPlaceSearch} onPlaceChanged={onPlaceChanged}>
                      <input type="text" placeholder="Search location on Google Maps" style={searchInputStyle} />
                    </Autocomplete>
                    {hasSelectedLocation ? (
                      <Box sx={{ height: 180, width: '100%', borderRadius: 1, overflow: 'hidden' }}>
                        <GoogleMap
                          mapContainerStyle={{ width: '100%', height: '100%' }}
                          center={{ lat: selectedLat, lng: selectedLng }}
                          zoom={16}
                          options={{
                            mapTypeControl: false,
                            fullscreenControl: false,
                            streetViewControl: false,
                          }}
                        >
                          <Marker position={{ lat: selectedLat, lng: selectedLng }} />
                        </GoogleMap>
                      </Box>
                    ) : null}
                    {formErrors.location ? (
                      <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                        {formErrors.location}
                      </Typography>
                    ) : null}
                  </Box>
                ) : (
                  <Box display="flex" justifyContent="center" py={0.5}>
                    <CircularProgress size={20} />
                  </Box>
                )
              ) : (
                <Alert severity="info">Google Maps search is unavailable. Configure REACT_APP_MAP_KEY.</Alert>
              )}
            </>
          ) : null}
          <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
            <Button type="button" variant="outlined" fullWidth onClick={closeModal} sx={{ borderRadius: '5px' }}>
              Cancel
            </Button>
            <Button type="submit" fullWidth disabled={saving} sx={{ borderRadius: '5px' }}>
              {saving ? 'Saving…' : mode === 'edit' ? 'Save changes' : 'Save address'}
            </Button>
          </Box>
        </Box>
      </Modal>
      <Modal open={deleteConfirmOpen} onClose={closeDeleteConfirm} title="Delete address">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to delete this address?
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              type="button"
              variant="outlined"
              fullWidth
              onClick={closeDeleteConfirm}
              disabled={Boolean(deletingAddressId)}
              sx={{ borderRadius: '5px' }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              fullWidth
              color="error"
              onClick={onDelete}
              disabled={Boolean(deletingAddressId)}
              sx={{ borderRadius: '5px' }}
            >
              {deletingAddressId ? 'Deleting…' : 'Delete'}
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}
